import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "../../../components/ui-components/Toast.jsx";
import { secondaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import { validateRecipients, formatScheduleSummary } from "../../reports/scheduleUtils.js";
import { listSchedulesByType } from "../../data-imports/api.js";
import { useCapabilities } from "../../../authz/useCapabilities";
import { CAPS } from "../../../authz/capabilities";
import { updateBoardReport } from "../document/boardReportApi.js";
import ReportDetailsFields from "../ReportDetailsFields.jsx";
import BoardReportScheduleModal, { JOB_TYPE, findScheduleForReport } from "../BoardReportScheduleModal.jsx";

function monthRange(p) {
  const base = dayjs(`${p}-01`);
  return [base.startOf("month"), base.endOf("month")];
}

/**
 * SettingsPanel - the Settings tab of the report home: report details (name/type/period) plus
 * delivery (recipients + schedule) in one place, replacing the separate /config screen and the
 * disconnected Schedule modal trigger. Reuses ReportDetailsFields (shared with the create form) so
 * the two never drift. Save is a full-replace PUT (backend has no partial merge), so it preserves
 * type/scope/manifest_override from the loaded report.
 *
 * The "Save changes" primary action lives in the page header (BoardReportHome): this panel exposes
 * save() through its ref and reports its in-flight state via onSavingChange so the header button can
 * show "Saving…" / disable itself.
 */
const SettingsPanel = forwardRef(({ id, report, onSaved, onSavingChange }, ref) => {
  const navigate = useNavigate();
  const { capabilities } = useCapabilities();
  // Editing the board-report template is a write surface gated the same as the
  // /board-reports/:id/template route's underlying API (CapReportsBoardManage).
  const canEditTemplate = capabilities.includes(CAPS.REPORTS_BOARD_MANAGE);
  const initialPeriod = report?.period && /^\d{4}-\d{2}$/.test(report.period) ? report.period : null;
  const [name, setName] = useState(report?.name || "");
  const [rangeStart, setRangeStart] = useState(initialPeriod ? monthRange(initialPeriod)[0] : null);
  const [rangeEnd, setRangeEnd] = useState(initialPeriod ? monthRange(initialPeriod)[1] : null);
  const [recipients, setRecipients] = useState(
    Array.isArray(report?.recipients)
      ? report.recipients.map((r) => ({ email: r.email || "", name: r.name || "" }))
      : []
  );
  const [errors, setErrors] = useState({});
  const [showSchedule, setShowSchedule] = useState(false);

  // Load the report's existing delivery schedule so the card can show it at a
  // glance (frequency + status) instead of hiding it inside the modal. Best-effort:
  // a load failure just falls back to a neutral message, never blocking the tab.
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleError, setScheduleError] = useState(false);

  const loadSchedule = useCallback(async () => {
    if (!id) return;
    setScheduleLoading(true);
    setScheduleError(false);
    try {
      const response = await listSchedulesByType(JOB_TYPE);
      const list = response?.schedules || response?.data || response || [];
      setSchedule(findScheduleForReport(Array.isArray(list) ? list : [], id));
    } catch (err) {
      setScheduleError(true);
    } finally {
      setScheduleLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const isSchedulePaused = schedule?.current_status === "paused";
  const scheduleSummary = schedule
    ? formatScheduleSummary(schedule?.schedule_expression || schedule?.cron || "")
    : "";

  const period = rangeStart ? rangeStart.format("YYYY-MM") : "";

  const validate = () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = "Name is required.";
    if (!period) {
      nextErrors.period = "Period is required.";
    } else if (!/^\d{4}-\d{2}$/.test(period)) {
      nextErrors.period = "Use the YYYY-MM format.";
    }
    if (recipients.length > 0) {
      const validation = validateRecipients(recipients.map((r) => r.email));
      if (!validation.isValid) nextErrors.recipients = "One or more recipient emails are invalid.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Report save-in-flight state up to the header, which hosts the Save button.
  const setSaving = (v) => {
    if (onSavingChange) onSavingChange(v);
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await updateBoardReport(id, {
        name: name.trim(),
        type: report?.type || "monthly_summary",
        period,
        scope: report?.scope || {},
        recipients: recipients.map((r) => ({ email: r.email, name: r.name || "" })),
        manifest_override: report?.manifest_override || {},
      });
      toast.success("Report updated");
      if (onSaved) onSaved();
    } catch (err) {
      toast.error("Failed to update report");
    } finally {
      setSaving(false);
    }
  };

  // Expose save() so the header's "Save changes" button can trigger it.
  useImperativeHandle(ref, () => ({ save: handleSave }));

  return (
    <div className="max-w-2xl space-y-6">
      <ReportDetailsFields
        name={name}
        onNameChange={setName}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        setRangeStart={setRangeStart}
        setRangeEnd={setRangeEnd}
        recipients={recipients}
        onRecipientsChange={setRecipients}
        errors={errors}
      />

      <div className="bg-card border border-card-bd rounded-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="text-sm font-medium text-text">Schedule</span>
            <p className="mt-1 text-sm text-text-2">
              Generate and email this report automatically on a recurring schedule.
            </p>
          </div>
          <button type="button" className={secondaryButtonCls} onClick={() => setShowSchedule(true)}>
            {schedule ? "Manage schedule" : "Set up schedule"}
          </button>
        </div>

        {/* Current schedule at a glance — no need to open the modal to see it. */}
        <div className="mt-4 border-t border-border pt-4">
          {scheduleLoading ? (
            <p className="text-sm text-text-3">Loading schedule…</p>
          ) : scheduleError ? (
            <p className="text-sm text-text-3">Couldn’t load the current schedule.</p>
          ) : schedule ? (
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  isSchedulePaused ? "bg-surface-2 text-text-2" : "bg-success-soft text-success"
                }`}
              >
                {isSchedulePaused ? "Paused" : "Active"}
              </span>
              <span className="text-sm text-text">{scheduleSummary}</span>
            </div>
          ) : (
            <p className="text-sm text-text-3">
              No schedule yet — this report is generated only when you create a run manually.
            </p>
          )}
        </div>
      </div>

      {canEditTemplate && (
        <div className="bg-card border border-card-bd rounded-card p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="text-sm font-medium text-text">Template</span>
              <p className="mt-1 text-sm text-text-2">
                Edit the report&apos;s block layout and data bindings. Admin only — changes apply to
                every future run.
              </p>
            </div>
            <button
              type="button"
              className={secondaryButtonCls}
              onClick={() => navigate(`/board-reports/${id}/template`)}
            >
              Edit template
            </button>
          </div>
        </div>
      )}

      <BoardReportScheduleModal
        show={showSchedule}
        boardReportId={id}
        boardReportName={name}
        defaultRecipients={recipients}
        onClose={() => {
          setShowSchedule(false);
          // Reflect any create/edit/pause/delete done in the modal.
          loadSchedule();
        }}
      />
    </div>
  );
});

SettingsPanel.displayName = "SettingsPanel";

SettingsPanel.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  report: PropTypes.object,
  onSaved: PropTypes.func,
  onSavingChange: PropTypes.func,
};

export default SettingsPanel;
